/**
 * Certificate layout — shared contract between the admin editor and the canvas
 * renderer in RunnerDetailsModal.
 *
 * The certificate is drawn on a fixed 1200x1600 canvas. Positions are stored per
 * event so each race can place its text over its own background artwork, and any
 * field left unset falls back to the default below — an event that never touches
 * these settings keeps rendering exactly as it did before this existed.
 *
 * Blocks, not individual strings: a label and its value move together, because
 * asking an operator to align "OVERALL RANK" separately from the number under it
 * is a trap, not a feature.
 */

// Reference canvas. Every stored y/size/offset below is expressed against THIS
// size — see resolveCertLayout, which scales them to whatever canvas an event
// actually uses. Changing these two numbers would move every existing layout.
export const CERT_W = 1200;
export const CERT_H = 1600;

/**
 * Canvas an event actually renders on.
 *
 * Clients deliver finished artwork and they do not all use 3:4 portrait; an A4
 * landscape background stretched onto 1200x1600 comes out vertically squashed,
 * with warped logos and type. So the canvas follows the artwork rather than the
 * artwork being forced to follow the canvas.
 *
 * Falls back to the reference size when an event has no explicit dimensions,
 * which is what every pre-existing event does.
 */
export const resolveCertSize = (stored) => {
  const w = Number(stored?.canvasWidth);
  const h = Number(stored?.canvasHeight);
  const ok = (n) => Number.isFinite(n) && n >= 200 && n <= 6000;
  return ok(w) && ok(h) ? { w: Math.round(w), h: Math.round(h) } : { w: CERT_W, h: CERT_H };
};

/**
 * Fonts an operator can pick per block.
 *
 * Web-safe only, deliberately: a client's artwork is usually set in a licensed
 * typeface we do not have and cannot redistribute, so the honest options are
 * "approximate it" or "let the artwork carry that text". Impact and Arial Black
 * are here because condensed heavy caps are what finisher certificates use for
 * names and times.
 */
export const CERT_FONTS = [
  { value: "Arial", label: "Arial" },
  { value: "Arial Black", label: "Arial Black" },
  { value: "Impact", label: "Impact" },
  { value: "Helvetica", label: "Helvetica" },
  { value: "Georgia", label: "Georgia" },
  { value: "Times New Roman", label: "Times" },
  { value: "Courier New", label: "Courier" },
  { value: "Verdana", label: "Verdana" },
  { value: "Trebuchet MS", label: "Trebuchet" },
];

/**
 * Uploaded fonts — the answer to "the artwork is set in Eurostile".
 *
 * A web-safe list can only approximate a client's typeface; when the client
 * hands over the font file (and holds its licence), the certificate can use the
 * real thing. Each event stores `customFonts: [{ family, url }]` in its layout,
 * and every renderer loads them before drawing.
 *
 * Loading is not optional: canvas never waits for a font. A face that is not in
 * document.fonts yet is silently drawn in the fallback, so the PNG a runner
 * downloads would be in Arial with nothing to say why.
 */
const fontLoads = new Map(); // url -> Promise<boolean>

export const loadCertFonts = (fonts) => {
  if (typeof document === "undefined" || typeof FontFace === "undefined") {
    return Promise.resolve();
  }
  const list = Array.isArray(fonts) ? fonts.filter((f) => f?.family && f?.url) : [];
  return Promise.all(
    list.map((f) => {
      const key = `${f.family}|${f.url}`;
      if (!fontLoads.has(key)) {
        const face = new FontFace(f.family, `url("${f.url}")`);
        fontLoads.set(
          key,
          face
            .load()
            .then((loaded) => {
              document.fonts.add(loaded);
              return true;
            })
            .catch((err) => {
              // A missing or blocked file falls back to Arial rather than
              // failing the certificate; forget it so a later attempt retries.
              console.warn(`[certificate] font "${f.family}" failed to load:`, err?.message || err);
              fontLoads.delete(key);
              return false;
            }),
        );
      }
      return fontLoads.get(key);
    }),
  );
};

/** Font dropdown entries: the built-in list plus this event's uploads. */
export const certFontOptions = (customFonts) => [
  ...(Array.isArray(customFonts) ? customFonts : [])
    .filter((f) => f?.family)
    .map((f) => ({ value: f.family, label: `${f.family} (uploaded)` })),
  ...CERT_FONTS,
];

/**
 * Per-block colours, alignment and typography.
 *
 * Every block carries its own `color` because backgrounds are the client's, not
 * ours: dark artwork makes the default near-black type invisible, and no amount
 * of repositioning fixes that. Blocks that draw a label above their value carry
 * a second `labelColor`, so "OVERALL RANK" can stay muted while the number sits
 * in the event's accent colour.
 *
 * `align` + `x` exist because finished client artwork does not put its dynamic
 * values on the centre line. A template with RANK bottom-left and TIMING RESULT
 * bottom-right cannot be expressed by a centred renderer at all — which is what
 * this was before. `x` is null for "use the centre", so untouched events keep
 * their old centred placement.
 *
 * `prefix`/`suffix`/`pad` shape the value itself: templates routinely print a
 * rank as `[001]`, and padding is not something an operator can fake by typing.
 *
 * The defaults below are the exact greys, fonts and positions the renderer used
 * before any of this was configurable, so an untouched event renders unchanged.
 */
const TEXT_BASE = { font: "Arial", bold: true, align: "center", x: null, prefix: "", suffix: "" };

export const DEFAULT_CERT_LAYOUT = {
  // Header band behind the event name. Height 0 hides it — useful when the
  // background image already carries its own header.
  headerHeight: 250,
  showHeaderBand: true,

  // textCase mirrors what the renderer used to hardcode, so untouched events
  // keep their existing capitalisation.
  eventName: { ...TEXT_BASE, y: 110, size: 60, color: "#111827", show: true, textCase: "upper" },
  subtitle: { ...TEXT_BASE, y: 170, size: 30, text: "OFFICIAL RACE RESULT", color: "#6B7280", show: true },
  runnerName: { ...TEXT_BASE, y: 400, size: 90, color: "#000000", show: true, textCase: "title" },
  categoryBib: { ...TEXT_BASE, y: 470, size: 40, color: "#9CA3AF", bold: false, show: true },
  finishLabel: { ...TEXT_BASE, y: 580, size: 30, text: "FINISH TIME", color: "#9CA3AF", show: true },
  finishTime: { ...TEXT_BASE, y: 710, size: 140, color: "#111827", show: true, pad: 0 },

  /**
   * Legacy symmetric rank pair. Superseded by overallRank/genderRank below, but
   * kept and still honoured so events laid out before the split keep rendering:
   * `show` defaults false here and the two independent blocks take over, while
   * an event that explicitly stored `ranks.show: true` gets the old behaviour.
   */
  ranks: { y: 880, size: 60, offset: 200, show: false, color: "#374151", labelColor: "#9CA3AF" },

  // Independent rank blocks. Separate rather than a mirrored pair because real
  // templates place them asymmetrically — the two columns in the WANITRAIL
  // design are not equidistant from the centre line.
  overallRank: {
    ...TEXT_BASE,
    y: 880,
    x: 400,
    size: 60,
    show: true,
    color: "#374151",
    label: "OVERALL RANK",
    labelSize: 24,
    labelGap: 40,
    labelColor: "#9CA3AF",
    showLabel: true,
    pad: 0,
    // "1/100": the placing over the number of placings awarded. Off by
    // default so existing certificates keep printing a bare rank.
    showTotal: false,
    totalSep: "/",
  },
  genderRank: {
    ...TEXT_BASE,
    y: 880,
    x: 800,
    size: 60,
    show: true,
    color: "#374151",
    label: "GENDER RANK",
    labelSize: 24,
    labelGap: 40,
    labelColor: "#9CA3AF",
    showLabel: true,
    pad: 0,
    // "1/100": the placing over the number of placings awarded. Off by
    // default so existing certificates keep printing a bare rank.
    showTotal: false,
    totalSep: "/",
  },

  // Category on its own, for artwork that gives it its own panel — the
  // Indonesia Ultra design sets it in a banner near the top, nowhere near the
  // BIB, so the combined `categoryBib` block cannot reach both places.
  categoryOnly: {
    ...TEXT_BASE,
    y: 560,
    x: 600,
    size: 56,
    show: false,
    color: "#374151",
    label: "CATEGORY",
    labelSize: 24,
    labelGap: 40,
    labelColor: "#9CA3AF",
    showLabel: false,
  },

  // BIB on its own. `categoryBib` prints "CATEGORY  |  BIB: 1234", which suits a
  // plain certificate but not artwork that already heads the space "BIB NUMBER"
  // and leaves a hole for the digits.
  bibOnly: {
    ...TEXT_BASE,
    y: 1030,
    x: 900,
    size: 60,
    show: false,
    color: "#374151",
    label: "BIB NUMBER",
    labelSize: 24,
    labelGap: 40,
    labelColor: "#9CA3AF",
    showLabel: true,
  },

  // Distance covered. A timed race is won on distance, not on a clock that is
  // the same for everyone — an Indonesia Ultra 24H finisher's achievement is
  // "181.35 km", so the certificate has to be able to say it. Off by default:
  // only looping categories measure a lane, and a road race has nothing to put
  // here.
  distance: {
    ...TEXT_BASE,
    // Centre of its own row below the ranks. x:1200 put it on the right edge of
    // a 1200-wide canvas, so the block was there but invisible — and the ranks
    // already occupy x:400 and x:800 on the row above.
    y: 1030,
    x: 600,
    size: 60,
    show: false,
    color: "#374151",
    label: "DISTANCE COVERED",
    labelSize: 24,
    labelGap: 40,
    labelColor: "#9CA3AF",
    showLabel: true,
    // No unit by default: artwork that leaves a hole for the distance usually
    // prints "KM" beside it already, and a rendered suffix would double it.
    // Add one in the designer for a certificate that needs it.
  },

  // Horizontal rule above the ranks. Its own block so a custom background can
  // turn it off without also losing the ranks, or move it independently.
  divider: { y: 780, show: true, color: "#D1D5DB" },

  splits: {
    y: 1050,
    size: 24,
    width: 900,
    show: true,
    color: "#111827",
    labelColor: "#374151",
    font: "Arial",
  },

  // Off by default: the bar is our own branding stamped across the bottom of
  // artwork the client paid a designer for, which is not something an operator
  // should have to discover and switch off on every event.
  footer: { text: "TIMING.VIONIXASIA.COM", show: false, color: "#FFFFFF", bgColor: "#111827" },

  // Uploaded typefaces, [{ family, url }]. Listed here so resolveCertLayout
  // carries them through — it only copies keys the defaults know about.
  customFonts: [],
};

/**
 * Letter case applied to a block's value before drawing.
 *
 * The renderer used to hardcode this — names forced to Title Case, the event
 * name forced to UPPERCASE — which fights any artwork that sets its names in
 * full caps. `title` is the historical default for names so existing events are
 * unaffected; `none` passes the stored value through untouched.
 */
export const CERT_CASES = [
  { value: "none", label: "As entered" },
  { value: "upper", label: "UPPERCASE" },
  { value: "title", label: "Title Case" },
  { value: "lower", label: "lowercase" },
];

export const applyCertCase = (s, mode) => {
  switch (mode) {
    case "upper":
      return s.toUpperCase();
    case "lower":
      return s.toLowerCase();
    case "title":
      return s
        .trim()
        .toLowerCase()
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    default:
      return s;
  }
};

/**
 * Format a value for drawing: apply letter case, zero-pad, then wrap in the
 * block's prefix/suffix. `[001]` is prefix "[", pad 3, suffix "]".
 *
 * `total` is the field size for a rank block. With `showTotal` on it is
 * appended inside the affixes — "1/100", or "[001/100]". Only the placing is
 * padded: the total is a count, not a slot to fill. Dropped when the total is
 * unknown or the runner has no placing, so a "-" never reads as "-/100".
 */
export const formatCertValue = (raw, block, total) => {
  let s = raw == null ? "" : String(raw);
  if (block?.textCase) s = applyCertCase(s, block.textCase);
  const isPlacing = /^\d+$/.test(s);
  const pad = Number(block?.pad) || 0;
  if (pad > 0 && isPlacing) s = s.padStart(pad, "0");
  const n = Number(total);
  if (block?.showTotal && isPlacing && Number.isFinite(n) && n > 0) {
    s = `${s}${block.totalSep ?? "/"}${n}`;
  }
  return `${block?.prefix || ""}${s}${block?.suffix || ""}`;
};

/**
 * Resolve a block's draw x and canvas textAlign.
 *
 * Takes an ALREADY-SCALED block (the output of resolveCertLayout), so it only
 * chooses the point — scaling x is the scaler's job, and doing it here too
 * would apply the ratio twice.
 *
 * A null x means "centre of the canvas", which is what every layout authored
 * before alignment existed relies on.
 */
export const resolveCertX = (block, canvasW) => ({
  x: block?.x == null ? canvasW / 2 : block.x,
  align: block?.align || "center",
});

/**
 * CSS font-family list for a block's font. Quoted, because an uploaded family is
 * whatever the operator named it and an unquoted "Font 2" is not a valid
 * family; Arial behind it so a font that failed to load degrades rather than
 * dropping to the browser's serif default.
 */
export const certFamily = (font) => {
  const f = String(font || "Arial").replace(/["\\]/g, "");
  return f === "Arial" ? "Arial" : `"${f}", Arial`;
};

/** Canvas font string for a block. */
export const certFont = (block, size) =>
  `${block?.bold === false ? "" : "bold "}${size}px ${certFamily(block?.font)}`;

/**
 * Deep-merge a stored layout over the defaults, one level per block, then scale
 * it to the event's canvas.
 *
 * Positions are authored against the 1200x1600 reference and scaled here, so an
 * operator who has already placed their text keeps that placement when the
 * canvas changes — vertical measures (y, headerHeight) follow the height ratio,
 * horizontal ones (offset, width) the width ratio, and type size follows the
 * smaller of the two so it never overflows the narrow axis.
 */
export const resolveCertLayout = (stored, size) => {
  const out = { ...DEFAULT_CERT_LAYOUT };
  if (stored && typeof stored === "object") {
    Object.keys(DEFAULT_CERT_LAYOUT).forEach((k) => {
      const base = DEFAULT_CERT_LAYOUT[k];
      const val = stored[k];
      if (val === undefined || val === null) return;
      out[k] = base && typeof base === "object" && !Array.isArray(base)
        ? { ...base, ...(typeof val === "object" ? val : {}) }
        : val;
    });
  }

  const { w, h } = size || resolveCertSize(stored);
  if (w === CERT_W && h === CERT_H) return out;

  const sx = w / CERT_W;
  const sy = h / CERT_H;
  const ss = Math.min(sx, sy);
  const scaled = { ...out, headerHeight: Math.round((out.headerHeight || 0) * sy) };
  Object.keys(out).forEach((k) => {
    const v = out[k];
    if (!v || typeof v !== "object" || Array.isArray(v)) return;
    scaled[k] = {
      ...v,
      ...(typeof v.y === "number" ? { y: Math.round(v.y * sy) } : {}),
      // x is nullable ("centre"), so guard on type rather than truthiness —
      // Math.round(null) is 0, which would pin a centred block to the left edge.
      ...(typeof v.x === "number" ? { x: Math.round(v.x * sx) } : {}),
      ...(typeof v.size === "number" ? { size: Math.round(v.size * ss) } : {}),
      ...(typeof v.labelSize === "number" ? { labelSize: Math.round(v.labelSize * ss) } : {}),
      ...(typeof v.labelGap === "number" ? { labelGap: Math.round(v.labelGap * sy) } : {}),
      ...(typeof v.offset === "number" ? { offset: Math.round(v.offset * sx) } : {}),
      ...(typeof v.width === "number" ? { width: Math.round(v.width * sx) } : {}),
    };
  });
  return scaled;
};

/**
 * Colour presets, applied over whatever positions the operator has already set.
 *
 * These only touch colours — never y/size/show — because artwork placement and
 * artwork palette are separate problems, and an operator who has spent ten
 * minutes aligning blocks to a client template should not lose that work to
 * fix contrast.
 *
 * "On dark artwork" is the case the plain defaults get wrong: a photographic
 * background renders the near-black default type invisible, which is not a
 * placement bug and cannot be dragged away.
 */
export const CERT_COLOR_PRESETS = [
  {
    key: "dark",
    label: "On dark artwork",
    swatch: "#111827",
    colors: {
      eventName: { color: "#FFFFFF" },
      subtitle: { color: "#E5E7EB" },
      runnerName: { color: "#FFFFFF" },
      categoryBib: { color: "#E5E7EB" },
      finishLabel: { color: "#E5E7EB" },
      finishTime: { color: "#FFFFFF" },
      divider: { color: "#FFFFFF" },
      ranks: { color: "#FFFFFF", labelColor: "#E5E7EB" },
      overallRank: { color: "#FFFFFF", labelColor: "#E5E7EB" },
      genderRank: { color: "#FFFFFF", labelColor: "#E5E7EB" },
      distance: { color: "#FFFFFF", labelColor: "#E5E7EB" },
      bibOnly: { color: "#FFFFFF", labelColor: "#E5E7EB" },
      categoryOnly: { color: "#FFFFFF", labelColor: "#E5E7EB" },
      splits: { color: "#FFFFFF", labelColor: "#E5E7EB" },
    },
  },
  {
    key: "light",
    label: "On light artwork",
    swatch: "#F3F4F6",
    colors: {
      eventName: { color: "#111827" },
      subtitle: { color: "#6B7280" },
      runnerName: { color: "#000000" },
      categoryBib: { color: "#6B7280" },
      finishLabel: { color: "#6B7280" },
      finishTime: { color: "#111827" },
      divider: { color: "#D1D5DB" },
      ranks: { color: "#374151", labelColor: "#6B7280" },
      overallRank: { color: "#374151", labelColor: "#6B7280" },
      genderRank: { color: "#374151", labelColor: "#6B7280" },
      distance: { color: "#374151", labelColor: "#6B7280" },
      bibOnly: { color: "#374151", labelColor: "#6B7280" },
      categoryOnly: { color: "#374151", labelColor: "#6B7280" },
      splits: { color: "#111827", labelColor: "#374151" },
    },
  },
];

/** Merge a preset's colours into a stored layout, leaving positions untouched. */
export const applyCertColorPreset = (stored, preset) => {
  const out = { ...(stored || {}) };
  Object.entries(preset.colors).forEach(([key, patch]) => {
    out[key] = { ...DEFAULT_CERT_LAYOUT[key], ...(out[key] || {}), ...patch };
  });
  return out;
};

/**
 * Fields the admin form exposes, in the order they appear on the certificate.
 *
 * `color` names what the swatch edits (the ink, or the line for the divider);
 * `labelColor` adds a second swatch for blocks that draw a caption alongside
 * their value. The footer is draggable-less but still listed, because hiding
 * our own bottom bar is the single most common thing an operator wants to do
 * with client artwork.
 */
export const CERT_FIELDS = [
  { key: "eventName", label: "Event name", pos: true, size: true, font: true, align: true, toggle: true, textCase: true, color: "Text" },
  { key: "subtitle", label: "Subtitle", pos: true, size: true, font: true, align: true, toggle: true, textCase: true, text: true, color: "Text" },
  { key: "runnerName", label: "Runner name", pos: true, size: true, font: true, align: true, toggle: true, textCase: true, color: "Text" },
  { key: "categoryBib", label: "Category + BIB", pos: true, size: true, font: true, align: true, toggle: true, textCase: true, color: "Text" },
  { key: "finishLabel", label: "“Finish time” label", pos: true, size: true, font: true, align: true, toggle: true, text: true, color: "Text" },
  {
    key: "finishTime",
    label: "Finish time",
    pos: true,
    size: true,
    font: true,
    align: true,
    toggle: true,
    affix: true,
    color: "Text",
  },
  { key: "divider", label: "Divider line", pos: true, toggle: true, color: "Line" },
  {
    key: "overallRank",
    label: "Overall rank",
    pos: true,
    size: true,
    font: true,
    align: true,
    toggle: true,
    affix: true,
    pad: true,
    total: true,
    caption: true,
    color: "Number",
    labelColor: "Caption",
  },
  {
    key: "categoryOnly",
    label: "Category only",
    pos: true, size: true, font: true, align: true, toggle: true,
    textCase: true, affix: true, caption: true, color: "Text", labelColor: "Caption",
  },
  {
    key: "bibOnly",
    label: "BIB only",
    pos: true, size: true, font: true, align: true, toggle: true,
    affix: true, caption: true, color: "Number", labelColor: "Caption",
  },
  {
    key: "distance",
    label: "Distance covered",
    pos: true,
    size: true,
    font: true,
    align: true,
    toggle: true,
    affix: true,
    caption: true,
    color: "Number",
    labelColor: "Caption",
  },
  {
    key: "genderRank",
    label: "Gender rank",
    pos: true,
    size: true,
    font: true,
    align: true,
    toggle: true,
    affix: true,
    pad: true,
    total: true,
    caption: true,
    color: "Number",
    labelColor: "Caption",
  },
  {
    key: "splits",
    label: "Splits",
    pos: true,
    size: true,
    width: true,
    font: true,
    toggle: true,
    color: "Time",
    labelColor: "Name",
  },
  { key: "footer", label: "Footer bar", toggle: true, text: true, color: "Text", bgColor: "Bar" },
];

/**
 * Layout presets — starting points for a whole certificate, not just colours.
 *
 * "Values only over artwork" is the case that finished client designs actually
 * need: the artwork already carries the event name, the date, the headings and
 * the signatures, so the renderer's job shrinks to dropping three values into
 * the holes left for them. Everything else is switched off rather than nudged
 * out of sight, because a hidden block cannot collide with the design.
 */
export const CERT_LAYOUT_PRESETS = [
  {
    key: "valuesOnly",
    label: "Values only over artwork",
    hint: "Name, rank and time only — the artwork supplies every other line.",
    layout: {
      showHeaderBand: false,
      headerHeight: 0,
      eventName: { show: false },
      subtitle: { show: false },
      categoryBib: { show: false },
      finishLabel: { show: false },
      divider: { show: false },
      splits: { show: false },
      ranks: { show: false },
      footer: { show: false },
      runnerName: { show: true, align: "center", x: null, color: "#FFFFFF" },
      overallRank: { show: true, showLabel: false, color: "#FFFFFF" },
      genderRank: { show: false },
      finishTime: { show: true, color: "#FFFFFF" },
      // Captions off: this preset exists for artwork that already prints the
      // headings, so a rendered caption would sit on top of the printed one.
      distance: { show: true, showLabel: false, color: "#FFFFFF" },
      bibOnly: { show: true, showLabel: false, color: "#FFFFFF" },
      categoryOnly: { show: true, showLabel: false, color: "#FFFFFF" },
    },
  },
  {
    /**
     * Two captioned values side by side — the layout most finisher templates
     * use along the bottom: a placing on one side, the time on the other, each
     * under its own heading and bracketed. Positions are a starting point to
     * drag from, not a claim about any particular artwork.
     */
    key: "rankAndTime",
    label: "Rank + time, side by side",
    hint: "Captioned columns with bracketed values, e.g. [001] and [00:00:01].",
    layout: {
      showHeaderBand: false,
      headerHeight: 0,
      eventName: { show: false },
      subtitle: { show: false },
      categoryBib: { show: false },
      finishLabel: { show: false },
      divider: { show: false },
      splits: { show: false },
      ranks: { show: false },
      footer: { show: false },
      genderRank: { show: false },
      runnerName: {
        show: true,
        align: "center",
        x: null,
        y: 830,
        size: 96,
        color: "#FFFFFF",
      },
      overallRank: {
        show: true,
        align: "center",
        x: 390,
        y: 1010,
        size: 62,
        color: "#FFFFFF",
        prefix: "[",
        suffix: "]",
        pad: 3,
        showLabel: true,
        label: "RANK",
        labelSize: 26,
        // Caption sits ABOVE the value here, which a negative gap expresses
        // without needing a second positioning mode.
        labelGap: -78,
        labelColor: "#FFFFFF",
      },
      finishTime: {
        show: true,
        align: "center",
        x: 810,
        y: 1010,
        size: 62,
        color: "#FFFFFF",
        prefix: "[",
        suffix: "]",
      },
    },
  },
];

/** Merge a layout preset over a stored layout, block by block. */
export const applyCertLayoutPreset = (stored, preset) => {
  const out = { ...(stored || {}) };
  Object.entries(preset.layout).forEach(([key, patch]) => {
    if (patch && typeof patch === "object" && !Array.isArray(patch)) {
      out[key] = { ...DEFAULT_CERT_LAYOUT[key], ...(out[key] || {}), ...patch };
    } else {
      out[key] = patch;
    }
  });
  return out;
};
